// File: src/pages/UnauthorizedPage.jsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthProvider";

/* ───────────────── helpers ───────────────── */
function safeDecodeReturnTo(v) {
  try {
    return decodeURIComponent(String(v || ""));
  } catch {
    return String(v || "");
  }
}

function isSafeInternalPath(p) {
  const s = String(p || "").trim();
  if (!s) return false;
  if (!s.startsWith("/")) return false;
  if (s.startsWith("//")) return false;

  // avoid loops / dead ends
  if (s === "/login" || s.startsWith("/login")) return false;
  if (s === "/forbidden" || s.startsWith("/forbidden")) return false;
  if (s === "/unauthorized" || s.startsWith("/unauthorized")) return false;

  return true;
}

function buildFromState(from) {
  try {
    if (!from) return "";
    const p = String(from.pathname || "");
    const s = String(from.search || "");
    const h = String(from.hash || "");
    return `${p}${s}${h}` || "";
  } catch {
    return "";
  }
}

function pickSafeTarget(raw) {
  const p = String(raw || "").trim();

  // Never loop back into platform admin pages on denial
  if (p === "/admin" || p.startsWith("/admin/")) return "/dashboard";

  if (isSafeInternalPath(p)) return p;
  return "/dashboard";
}

export default function UnauthorizedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const user = auth?.user || null;

  const target = useMemo(() => {
    const stateReturnTo = safeDecodeReturnTo(location.state?.returnTo);

    let queryReturnTo = "";
    try {
      const params = new URLSearchParams(location.search || "");
      queryReturnTo = safeDecodeReturnTo(params.get("returnTo"));
    } catch {}

    const fromState = buildFromState(location.state?.from);

    const chosen = stateReturnTo || queryReturnTo || fromState || "";
    return pickSafeTarget(chosen);
  }, [location.state, location.search]);

  const goBack = () => {
    if (!user) return navigate("/login", { replace: true });
    return navigate(target, { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white px-6">
      <h1 className="text-3xl font-bold mb-4">🚫 Access Denied</h1>

      <p className="mb-6 text-center max-w-md text-white/80">
        You don’t have permission to view this page. If you believe this is an error, please contact your system administrator.
      </p>

      <button
        onClick={goBack}
        className="px-6 py-2 bg-red-500 hover:bg-red-600 rounded-md"
        type="button"
      >
        Go Back to Dashboard
      </button>

      {import.meta?.env?.DEV ? (
        <div className="mt-4 text-xs text-white/50">
          <div>reason: {String(location.state?.reason || "")}</div>
          <div>target: {target}</div>
        </div>
      ) : null}
    </div>
  );
}